<script setup lang="ts">
  import { markFirstAdRequest } from '../../utils/consent/firstAdRequest'
  import { onMounted } from 'vue'
  import type { NtpAdEvent } from '../../composables/analytics/types'
  import type { AssertiveYieldManagerEnv, Slot } from '../../types/assertive'
  import { getIndexOfPlacement } from '../../utils/ad-ops/adFunctions'
  import {
    appDrivesPlacement,
    DISPLAY_MARGIN_FLAG,
    driveSlot,
    marginPx,
    RENDER_MARGIN_FLAG,
    tagRumWithAppOwned,
    tagRumWithMargins,
    VIEWPORT_EDGE,
    whenWithin,
    type SlotDriver,
    type SlotMargins,
  } from '../../services/adops/slotTiming'
  import {
    claimSlot,
    listenToSlots,
    ownsSlot,
    releaseSlot,
    slotState,
  } from '../../services/adops/slots'
  import {
    DEFAULT_REFRESH,
    REFRESH_MAX_FLAG,
    REFRESH_SECONDS_FLAG,
    refreshMax,
    refreshSeconds,
    refreshUnlessAyDoes,
    tagRumWithRefresh,
    unregisterFromRefresh,
    type RefreshPolicy,
  } from '../../services/adops/refresh'
  import debug from '../../utils/console'
  import { withReadyAyManager } from '../../utils/consent/adReadiness'
  import { withAyManager } from '../../utils/ad-ops/assertive-yield/queue'
  import {
    markConsentTiming,
    measureConsentTiming,
  } from '../../utils/consent/consentTiming'

  const logger = debug.withTag('Assertive')

  export interface AssertiveProps {
    placement: string
    tilePosition?: number
    lazyRender?: boolean
    sticky?: boolean
    adHeight?: string
    adWidth?: string
    adInstanceId?: string
    /** The app may refresh this placement (mapping `assertive.refresh`). */
    refresh?: boolean
  }

  const props = withDefaults(defineProps<AssertiveProps>(), {
    tilePosition: -1,
    sticky: false,
    adHeight: 'auto',
    adWidth: 'auto',
    adInstanceId: '',
    refresh: false,
  })

  type SlotFlags = SlotMargins & { refresh: RefreshPolicy }

  const DEFAULT_FLAGS: SlotFlags = {
    ...VIEWPORT_EDGE,
    refresh: DEFAULT_REFRESH,
  }

  const { adClicked, renderWhenCurrentElementIsVisible } = useAdFunctions()
  const { $ayLoaded, $ayManagerEnv } = useNuxtApp()
  const placementEl = useTemplateRef<HTMLElement | null>('placementEl')
  const placementClassName = ref<string | null>(props.placement)

  let renderStarted = false
  let instanceCode = ''
  const slotOwner = Symbol(props.placement)
  let displayedIn: HTMLElement | null = null
  let unmounted = false
  let cancelPendingDisplay = () => {}
  let flagsReady: Promise<SlotFlags> = Promise.resolve(DEFAULT_FLAGS)
  let stopDisplayWatch: (() => void) | null = null
  let slotDriver: SlotDriver | null = null

  function getEventData(): NtpAdEvent {
    return {
      ntp_tile_position: props.tilePosition,
      ntp_ad_brand: 'programmatic',
      ntp_ad_provider: 'programmatic',
      ntp_creative_id: instanceCode,
      ntp_placement_name: props.placement,
      ntp_ad_refresh: slotState(instanceCode).refreshed,
      ntp_ay_instance_code: instanceCode,
    }
  }

  let adEngaged = false
  let hasClicked = false

  function onMouseOver() {
    adEngaged = true
  }

  function onMouseOut() {
    adEngaged = false
    hasClicked = false
  }

  function isFocusInsideAd() {
    return placementEl.value?.contains(document.activeElement) ?? false
  }

  function onLeaveEvent() {
    if (!adEngaged || hasClicked || !isFocusInsideAd()) return
    hasClicked = true
    adClicked(getEventData(), instanceCode)
  }

  function onVisibilityChange() {
    if (document.visibilityState === 'hidden') onLeaveEvent()
  }

  const renderAYAd = async () => {
    if (!placementEl.value || renderStarted) return
    renderStarted = true

    const index = getIndexOfPlacement(props.placement, placementEl.value)

    placementClassName.value =
      index > 0
        ? `${props.placement} ${props.placement}-${index}`
        : props.placement

    markConsentTiming('first-visible-ad-wait-start')
    await $ayLoaded
    const container = placementEl.value
    if (unmounted || !container?.isConnected) return

    cancelPendingDisplay = withReadyAyManager(withAyManager, () => {
      const env = $ayManagerEnv
      if (!env || unmounted || !container.isConnected) return
      markConsentTiming('first-visible-ad-ready')
      measureConsentTiming(
        'first-visible-ad-wait',
        'first-visible-ad-wait-start',
        'first-visible-ad-ready'
      )
      displayedIn = container
      env.onEvent('afterDefineSlot', onAfterDefineSlot)
      markConsentTiming('first-visible-ad-display')
      markFirstAdRequest('display')
      tagRumWithAppOwned(env)
      listenToSlots()
      env.display(container)
      if (appDrivesPlacement(env, props.placement)) startDriver(container, env)
    })
  }

  // display() has started the auction; the app owns the render. The auction
  // can define the slot before the flags load, so a code that already arrived
  // is handed over.
  function startDriver(
    container: HTMLElement,
    env: Pick<AssertiveYieldManagerEnv, 'render'>
  ) {
    flagsReady.then((flags) => {
      if (unmounted) return
      slotDriver = driveSlot(container, flags.renderPx, env)
      if (instanceCode) slotDriver.defined(instanceCode)
    })
  }

  // Copies of a placement share its name, so claim the slot by containment.
  // Searched from the container because it may already be detached.
  function onAfterDefineSlot(code: string, slot: Slot) {
    if (instanceCode || !displayedIn) return
    const slotId = slot.getSlotElementId()
    const ownsSlot = Array.from(displayedIn.querySelectorAll('[id]')).some(
      (el) => el.id === slotId
    )
    if (!ownsSlot) return
    instanceCode = code
    claimSlot(code, slotOwner)
    slotDriver?.defined(code)
    // Defined after unmount, so nothing else will destroy it.
    if (unmounted) {
      destroyInstance()
      return
    }
    refreshIfAllowed(code)
  }

  // Whoever renders the slot, once the mapping opts it in.
  function refreshIfAllowed(code: string) {
    if (!props.refresh) return
    flagsReady.then((flags) => {
      if (unmounted) return
      withAyManager(() => {
        const env = $ayManagerEnv
        if (!env || unmounted || instanceCode !== code) return
        refreshUnlessAyDoes(code, props.placement, flags.refresh, env)
      })
    })
  }

  // Until AY defines the slot there is no code to destroy, so the listener
  // stays until it arrives.
  function destroyInstance() {
    slotDriver?.stop()
    withAyManager(() => {
      const code = instanceCode
      if (!code) return
      $ayManagerEnv?.offEvent('afterDefineSlot', onAfterDefineSlot)
      // The native fallback destroyed our slot and AY gave its code to another.
      if (!ownsSlot(code, slotOwner)) return
      unregisterFromRefresh(code)
      // The native fallback already destroyed it.
      const { fellBack } = slotState(code)
      releaseSlot(code)
      if (!fellBack) {
        $ayManagerEnv?.destroy([code], { useInstanceCodes: true })
      }
    })
  }

  onMounted(() => {
    placementEl.value?.addEventListener('mouseenter', onMouseOver, {
      passive: true,
    })
    placementEl.value?.addEventListener('mouseleave', onMouseOut, {
      passive: true,
    })
    window.addEventListener('blur', onLeaveEvent, { passive: true })
    window.addEventListener('pagehide', onLeaveEvent, { passive: true })
    window.addEventListener('beforeunload', onLeaveEvent, { passive: true })
    document.addEventListener('visibilitychange', onVisibilityChange, {
      passive: true,
    })

    flagsReady = resolveFlags()
    flagsReady.then((flags) => {
      tagRumWithMargins(flags)
      tagRumWithRefresh(flags.refresh)
    })
    // Watch for visibility now rather than after the manager loads, so a slot
    // that is only briefly in view while the feed lays out is not missed.
    renderWhenCurrentElementIsVisible(
      placementEl.value,
      props.lazyRender,
      props.sticky,
      renderAYAd
    )
    if (props.lazyRender) {
      displayAheadIfAppOwned().catch((err) => {
        logger.error('Unable to display AY slot', err)
      })
    }
  })

  async function resolveFlags(): Promise<SlotFlags> {
    try {
      // Read once the provider has loaded: an earlier read gets the default.
      const { loaded } = await useOpenFeature()
      const client = await loaded
      return {
        displayPx: marginPx(client.getNumberValue(DISPLAY_MARGIN_FLAG, 0)),
        renderPx: marginPx(client.getNumberValue(RENDER_MARGIN_FLAG, 0)),
        refresh: {
          max: refreshMax(
            client.getNumberValue(REFRESH_MAX_FLAG, DEFAULT_REFRESH.max)
          ),
          seconds: refreshSeconds(
            client.getNumberValue(REFRESH_SECONDS_FLAG, DEFAULT_REFRESH.seconds)
          ),
        },
      }
    } catch (err) {
      logger.warn('Slot flags unavailable, using their defaults', err)
      return DEFAULT_FLAGS
    }
  }

  // An app-owned slot is displayed, which starts its auction, at the display
  // margin. The visibility gate stays as the fallback; renderAYAd runs once.
  async function displayAheadIfAppOwned() {
    const [ayReady, flags] = await Promise.all([$ayLoaded, flagsReady])
    const container = placementEl.value
    if (!ayReady || unmounted || !container || flags.displayPx === 0) return
    // Inside the queue, like renderAYAd, so both read the settings AY settled on.
    withAyManager(() => {
      if (unmounted || !appDrivesPlacement($ayManagerEnv, props.placement)) {
        return
      }
      stopDisplayWatch = whenWithin(container, flags.displayPx, () => {
        renderAYAd().catch((err) => {
          logger.error('Unable to display AY slot', err)
        })
      })
    })
  }

  onUnmounted(() => {
    placementEl.value?.removeEventListener('mouseenter', onMouseOver)
    placementEl.value?.removeEventListener('mouseleave', onMouseOut)
    window.removeEventListener('blur', onLeaveEvent)
    window.removeEventListener('pagehide', onLeaveEvent)
    window.removeEventListener('beforeunload', onLeaveEvent)
    document.removeEventListener('visibilitychange', onVisibilityChange)
    unmounted = true
    cancelPendingDisplay()
    stopDisplayWatch?.()
    if (renderStarted) destroyInstance()
  })
</script>

<template>
  <div
    :id="props.placement"
    ref="placementEl"
    :class="placementClassName"
    :data-ay-manager-id="props.placement"
    :data-ad-instance-id="props.adInstanceId"
  />
</template>
